import { useState, useEffect, useCallback } from 'react';
import { verifyRazorpayPayment } from '../services/paymentService';

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Hook to handle Razorpay checkout
 * Loads the Razorpay script and provides a function to open checkout modal
 */
const useRazorpay = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if script is already loaded
    if (window.Razorpay) {
      setScriptLoaded(true);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => setScriptLoaded(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => console.error('Failed to load Razorpay script');
    document.body.appendChild(script);

    return () => {
      // Cleanup not needed for payment scripts
    };
  }, []);

  /**
   * Open Razorpay checkout modal
   * @param {Object} options - Checkout options from backend
   * @param {Function} onSuccess - Callback on successful payment
   * @param {Function} onFailure - Callback on payment failure
   */
  const openCheckout = useCallback(async (options, onSuccess, onFailure) => {
    if (!scriptLoaded) {
      onFailure(new Error('Razorpay script not loaded'));
      return;
    }

    if (!window.Razorpay) {
      onFailure(new Error('Razorpay not available'));
      return;
    }

    setLoading(true);

    const razorpayOptions = {
      key: options.keyId,
      amount: options.amount,
      currency: options.currency || 'INR',
      name: options.name || 'Kwikly Pro',
      description: options.description || 'Pro Access',
      order_id: options.orderId,
      prefill: options.prefill || {},
      notes: options.notes || {},
      theme: {
        color: '#6366f1' // Primary color matching the app
      },
      handler: async (response) => {
        try {
          // Verify payment with backend
          const result = await verifyRazorpayPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });

          setLoading(false);

          if (result.success) {
            onSuccess(result.data);
          } else {
            onFailure(new Error(result.error || 'Payment verification failed'));
          }
        } catch (err) {
          setLoading(false);
          onFailure(err);
        }
      },
      modal: {
        ondismiss: () => {
          setLoading(false);
          onFailure(new Error('Payment cancelled'));
        },
        escape: true,
        animation: true
      }
    };

    try {
      const razorpay = new window.Razorpay(razorpayOptions);
      razorpay.on('payment.failed', (response) => {
        setLoading(false);
        onFailure(new Error(response.error?.description || 'Payment failed'));
      });
      razorpay.open();
    } catch (err) {
      setLoading(false);
      onFailure(err);
    }
  }, [scriptLoaded]);

  return {
    scriptLoaded,
    loading,
    openCheckout
  };
};

export default useRazorpay;
